import { Component, InjectionToken, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AlertService } from '@shared/services/alert.service';
import { HarpValidators } from '@shared/utils/validators';
import { InputConfig } from '@shared/models/input-config.model';
import { TEXT_CONSTANTS } from '@shared/constants';
import { HarpLoginService } from '@login/services/harp-login.service';
import { NavigationService } from '@login/services/navigation.service';
import { AdoConfigService } from '@login/services/ado-config.service';
import { AdoConfig } from '@login/models/ado-config.model';
import { HarpStatusService } from '@login/services/harp-status.service';

import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import {
  supportedApplications,
  otherApplications,
} from '@login/util/supported-apps';
const UNEXPECTED_ERROR_MSG =
  'An unexpected error has occurred. Please try again later.';
const HARP_STATUS_ALERT_SERVICE = new InjectionToken<AlertService>(
  'modal.alert',
);
const ADMIN_TOOL_MSG = 'The User Triage Tool is now the User Admin Tool';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  providers: [{ provide: HARP_STATUS_ALERT_SERVICE, useClass: AlertService }],
})
export class LoginComponent implements OnInit {
  loginForm: UntypedFormGroup;
  userIdConfig: InputConfig;
  passwordConfig: InputConfig;
  currentApp: AdoConfig;
  supportedApplications = supportedApplications;
  otherApplications = otherApplications;
  pivRedirectUri: any = '';

  constructor(
    private modalService: NgbModal,
    private harpLoginService: HarpLoginService,
    public navigationService: NavigationService,
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    private adoConfigService: AdoConfigService,
    private harpStatusService: HarpStatusService,
    public harpStatusAlertService: AlertService,
  ) {}

  ngOnInit() {
    this.initForm();
    this.currentApp = this.adoConfigService.getCurrentApp();
    this.getHarpStatus();
  }

  initForm() {
    this.loginForm = this.fb.group({
      userId: [
        '',
        HarpValidators.required(
          TEXT_CONSTANTS.ERROR_MESSAGE_REQUIRED('User ID'),
        ),
        ,
      ],
      password: [
        '',
        HarpValidators.required(
          TEXT_CONSTANTS.ERROR_MESSAGE_REQUIRED('Password'),
        ),
      ],
    });

    this.userIdConfig = {
      name: 'userId',
      label: 'User ID',
      maxlength: 100,
      required: true,
      form: this.loginForm,
    };
    this.passwordConfig = {
      name: 'password',
      label: 'Password',
      type: 'password',
      required: true,
      form: this.loginForm,
    };
  }

  openModal(modalName) {
    this.modalService.open(modalName, {
      size: 'lg',
    });
  }

  onPIVLinkSelection() {
    // Save ADO cookie and get redirect URI
    this.pivRedirectUri =
      this.harpLoginService.setAdoCookiesAndUpdateRedirectUri();

    // Get PIV login URL
    this.harpLoginService.getAdoHomePageUrl('piv').subscribe({
      next: (res: any) => {
        if (!res || res.errorCode) {
          this.alertService.setAlert(UNEXPECTED_ERROR_MSG);
          return;
        }

        // Set PIV cookie with longer expiration
        if (res.url) {
          this.harpLoginService.setPivCookie();
        }

        // Construct redirect URL with proper parameter handling
        const redirectUrl = this.getPivCardRedirectURI(res.url);
        this.navigationService.goToUrl(redirectUrl);
      },
      error: error => {
        console.error('PIV login error:', error);
        this.alertService.setAlert(UNEXPECTED_ERROR_MSG);
      },
    });
  }

  private getPivCardRedirectURI(oktaURL: string): string {
    const fromURI = `${window.location.origin}/login/login-redirect-interim`;

    // Handle query parameters
    const searchParams = new URLSearchParams(window.location.search);
    const adoParam = searchParams.get('ADO');

    // Construct params object
    const params = new URLSearchParams();
    if (adoParam) {
      params.set('ADO', adoParam);
    }

    // Encode only once
    const queryString = params.toString();
    const encodedParams = queryString ? encodeURIComponent(queryString) : '';

    return `${oktaURL}?fromURI=${fromURI}/${encodedParams}`;
  }

  userLoginCheck() {
    this.loginForm.markAllAsTouched();
    if (this.loginForm?.valid) {
      console.log('DEPLOYMENT 0003');
      this.harpLoginService
        .checkLoginStatusAndRedirect({
          userId: this.userIdControl?.value,
          password: this.passwordControl?.value,
        })
        .subscribe(
          () => {},
          () => this.alertService.setAlert(UNEXPECTED_ERROR_MSG),
        );
    }
  }

  private getHarpStatus() {
    this.harpStatusService.getHarpStatus().subscribe({
      next: (res: any) => {
        if (
          res.msg !== null &&
          res.msg.length > 0 &&
          !res.msg.includes(ADMIN_TOOL_MSG)
        ) {
          this.harpStatusAlertService.setAlert(res.msg, res.type);
        }
      },
    });
  }

  get passwordControl(): AbstractControl {
    return this.loginForm?.get('password');
  }

  get userIdControl(): AbstractControl {
    return this.loginForm?.get('userId');
  }
}
